import { CommandType, ScriptCommands } from '../types/dashboard';

export const QUERY_LOG_UPLOADER_ID = 'steps.2.b.panel.tabs.gui.query-log';
export const QUERY_LOG_S3_UPLOAD_PREFIX = 'user-input/query-log/';

export const SCHEMA_UPLOAD_UPLOADER_ID = 'steps.1.c.panel.tabs.gui.schema-upload';
export const DEIDENTIFIED_DATA_UPLOADER_ID =
  'steps.4.c.panel.tabs.gui.schema-upload';
export const DEIDENTIFIED_DATA_S3_PREFIX_FALLBACK = 'de-identified-data/';
export const DEIDENTIFIED_DATA_DETAILS_API =
  '/schema-details?list=deidentified';

const UPLOAD_PREFIX_FALLBACK: Record<
  string,
  { run: (runId: number) => string; legacy: string }
> = {
  [SCHEMA_UPLOAD_UPLOADER_ID]: {
    run: (runId) => `user-input/schema/run-${runId}/`,
    legacy: 'user-input/schema/',
  },
  [QUERY_LOG_UPLOADER_ID]: {
    run: (runId) => `user-input/query-log/run-${runId}/`,
    legacy: QUERY_LOG_S3_UPLOAD_PREFIX,
  },
  [DEIDENTIFIED_DATA_UPLOADER_ID]: {
    run: (runId) => `de-identified-data/run-${runId}/`,
    legacy: DEIDENTIFIED_DATA_S3_PREFIX_FALLBACK,
  },
};

function withTrailingSlash(prefix: string): string {
  const trimmed = prefix.trim();
  if (!trimmed) return '';
  return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
}

/** Run-scoped S3 prefix for dashboard uploaders when the API prefix is missing or wrong. */
export function resolveUploadPrefix(
  uploaderId: string,
  api: string,
  prefixFromApi: string,
  runId: number | null | undefined
): string {
  const trimmed = (prefixFromApi ?? '').trim();
  const isDeidentified =
    uploaderId === DEIDENTIFIED_DATA_UPLOADER_ID ||
    api.includes('list=deidentified');
  const apiPrefixOk = trimmed && !(isDeidentified && trimmed.includes('/schema/'));

  if (apiPrefixOk) {
    return withTrailingSlash(trimmed);
  }

  const fallback = UPLOAD_PREFIX_FALLBACK[uploaderId];
  if (fallback) {
    return runId != null ? fallback.run(runId) : fallback.legacy;
  }

  return withTrailingSlash(trimmed);
}

export const queryLogAwsCliInstallScript: CommandType[] = [
  {
    commands: ['aws --version'],
    title: 'Check AWS CLI',
    description:
      'Run this to confirm AWS CLI is installed. If you see an error, install or update it using the installation link above.',
  },
];

const QUERY_LOG_OBJECT_KEY_PLACEHOLDER = '<OBJECT_KEY>';
const QUERY_LOG_LOCAL_PATH_PLACEHOLDER = '<LOCAL_FILE_PATH>';

export function buildQueryLogS3DestinationUri(
  bucket: string,
  prefix: string,
  objectKeyPlaceholder: string = QUERY_LOG_OBJECT_KEY_PLACEHOLDER
): string {
  const normalizedPrefix = prefix.replace(/^\//, '');
  return `s3://${bucket}/${normalizedPrefix}${objectKeyPlaceholder}`;
}

export function buildQueryLogAwsS3CpCommand(
  bucket: string,
  prefix: string
): string {
  const uri = buildQueryLogS3DestinationUri(bucket, prefix);
  return `aws s3 cp "${QUERY_LOG_LOCAL_PATH_PLACEHOLDER}" "${uri}" --cli-read-timeout 0`;
}

export const queryLogS3TransferConfigureSnippet = `aws configure set default.s3.max_concurrent_requests 20
aws configure set default.s3.max_queue_size 10000
aws configure set default.s3.multipart_threshold 64MB
aws configure set default.s3.multipart_chunksize 16MB`;

function escapeQueryLogShellDoubleQuoted(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

export function buildQueryLogTemporaryCredentialShellExports(credentials: {
  access_key_id: string;
  secret_access_key: string;
  session_token: string;
}): string {
  return [
    `export AWS_ACCESS_KEY_ID="${escapeQueryLogShellDoubleQuoted(credentials.access_key_id)}"`,
    `export AWS_SECRET_ACCESS_KEY="${escapeQueryLogShellDoubleQuoted(credentials.secret_access_key)}"`,
    `export AWS_SESSION_TOKEN="${escapeQueryLogShellDoubleQuoted(credentials.session_token)}"`,
  ].join('\n');
}

export const staticCommandsData: ScriptCommands = {
  saveSchemaToFile: [
    {
      commands: [
        'mysqldump -u <username> -p <password> --no-data dbname > schema.sql',
      ],
      title: 'Save MySQL Schema to File',
      description: 'Command to save MySQL schema to a file',
    },
  ],
  saveSchemaInstallScript: [
    {
      commands: ['mysqldump --version'],
      title: 'Verify installation of mysqldump',
      description: '',
    },
  ],
  saveSchemaDownloadScript: [
    {
      commands: ['cd export_schema_mysql'],
      title: 'cd to export_schema_mysql',
      description: '',
    },
    {
      commands: [
        'curl -sSL https://install.python-poetry.org | python3 -',
        'poetry install',
        'poetry shell',
      ],
      title: 'Activate virtual env',
      description: '',
    },
    {
      commands: ['exsch --db_name <db_name> -h <host> -p <port> -u <db_user>'],
      title: 'Run script',
      description: '',
    },
  ],
  createQueryLogSection1: [
    {
      commands: [
        `CREATE EVENT SESSION [QueryPerformance] ON SERVER
      ADD EVENT sqlserver.sql_statement_completed(
          ACTION (sqlserver.sql_text, sqlserver.database_name, sqlserver.username)
      )
      ADD TARGET package0.asynchronous_file_target(
          SET filename = 'd:\\rdsdbdata\\log\\xe_query_performance.xel',
          max_file_size = 50 MB
      );`,
      ],
      title: 'Create Extended Events',
      description: '',
    },
    {
      commands: [
        'ALTER EVENT SESSION [QueryPerformance] ON SERVER STATE = START;',
      ],
      title: 'Start Extended Event',
      description: '',
    },
  ],
  createQueryLogSection2: [
    {
      commands: [''],
      title: 'Enable Query Logging',
      description: '',
    },
    {
      commands: [
        'ALTER EVENT SESSION [QueryPerformance] ON SERVER STATE = STOP;',
      ],
      title: 'Stop Extended Event',
      description: '',
    },
    {
      commands: [
        `DECLARE @sessionExists INT;
        SELECT @sessionExists = COUNT(*) 
        FROM sys.server_event_sessions 
        WHERE name = 'QueryPerformance';

        IF @sessionExists = 1
        BEGIN
            DROP EVENT SESSION QueryPerformance ON SERVER;
        END`,
      ],
      title: 'Drop Extended Event',
      description: '',
    },
  ],
  exportDatabase: [
    {
      commands: [
        '$ aws s3 cp myfolder s3://mybucket/myfolder --recursive',
        'upload: myfolder/file1.txt to s3://mybucket/myfolder/file1.txt',
        'upload: myfolder/subfolder/file.txt to s3://mybucket/myfolder/subfolder/file1.txt',
      ],
      title: 'Copy Files to S3 Bucket',
      description: 'Command to copy files to an S3 bucket recursively',
    },
    {
      commands: [
        '$ aws s3 sync myfolder s3://mybucket/myfolder --exclude # .tmp',
        'upload: myfolder/newfile.txt to s3://mybucket/myfolder/newfile.txt',
      ],
      title: 'Sync Files to S3 Bucket',
      description:
        'Command to sync files to an S3 bucket, excluding temporary files',
    },
  ],
  downloadScript: [
    {
      commands: ['sudo apt install mysql-client'],
      title: 'Install mysql server on linux',
      description: '',
    },
    {
      commands: [
        'sudo apt-get install unzip',
        'unzip script.zip -d <destination_folder>',
      ],
      title: 'Extract the package',
      description: '',
    },
    {
      commands: ['cd mysql'],
      title: 'move to mysql directory',
      description: '',
    },
    {
      commands: [
        'curl -sSL https://install.python-poetry.org | python3 -',
        'poetry install',
        'poetry shell',
      ],
      title: 'Activate virtual env',
      description: '',
    },
    {
      commands: [
        'exdbd --db_name <database_name> --db_schema <schema_name> --db_host <host_address> --db_port <port> --db_user <database_user>',
      ],
      title: 'Run script',
      description: '',
    },
  ],
  settingUpTheSystemCommandSection1: [
    {
      commands: ['python --version', 'pip --version'],
      title: '',
      description: '',
    },
  ],
  settingUpTheSystemCommandSection2: [
    {
      commands: ['pip install pipenv --user'],
      title: '',
      description: '',
    },
  ],
  settingUpTheSystemCommandSection3: [
    {
      commands: ['mysqldump --version'],
      title: '',
      description: '',
    },
  ],
  exportDataAndDeidentificationScript: [
    {
      commands: [
        'sudo apt-get install unzip',
        'unzip script.zip -d <destination_folder>',
      ],
      title: 'Extract the package',
      description: '',
    },
    {
      commands: ['cd deidentification'],
      title: 'move to deidentification directory',
      description: '',
    },
    {
      commands: [
        'curl -sSL https://install.python-poetry.org | python3 -',
        'poetry install',
        'poetry shell',
      ],
      title: 'Activate virtual env',
      description: '',
    },
    {
      commands: ['python3 script/main.py'],
      title: 'Run script',
      description: '',
    },
  ],
  shareDeidentifiedFileScript: [
    {
      commands: [
        '$ aws s3 cp myfolder s3://mybucket/myfolder --recursive',
        'upload: myfolder/file1.txt to s3://mybucket/myfolder/file1.txt',
        'upload: myfolder/subfolder/file.txt to s3://mybucket/myfolder/subfolder/file1.txt',
      ],
      title: 'Upload Files from S3 Bucket',
      description: 'Command to Upload files from an S3 bucket',
    },
  ],
  // PLACEHOLDERS
  queryLogExtractionSetupCommand1: [
    {
      commands: ['poetry --version'],
      title: 'Verify Installation of Poetry',
      description: 'Check that Poetry is installed and accessible, output should be like this: Poetry (version X.X.X)',
    },
  ],
  queryLogExtractionSetupCommand2: [
    {
      commands: ['python --version'],
      title: 'Verify Python Installation',
      description: 'Check that Python 3.11+ is installed and accessible',
    },
  ],
  exportQueryLogsScript: [
    {
      commands: ['cd export_query_logs'],
      title: 'Move to export query logs directory',
      description: '',
    },
    {
      commands: ['poetry shell'],
      title: 'Activate virtual env',
      description: 'Note: Run "poetry install" only once during initial setup if dependencies are not already installed.',
    },
    {
      commands: [
        'export-query-logs --db-type <DB_TYPE> --host <DB_HOSTNAME> --port <DB_PORT> --user <DB_USERNAME> --password <DB_PASSWORD> --database <DB_NAME> --mode export --duration <DURATION_IN_HOURS(DEFAULT: 24)>',
      ],
      title: 'Run script (Export Mode)',
      description: 'Runs the script in export mode: enables logging, waits for the specified duration, then disables logging and exports the logs.',
    },
  ],
  enableQueryLogsScript: [
    {
      commands: ['cd export_query_logs'],
      title: 'Move to export query logs directory',
      description: '',
    },
    {
      commands: ['poetry shell'],
      title: 'Activate virtual env',
      description: 'Note: Run "poetry install" only once during initial setup if dependencies are not already installed.',
    },
    {
      commands: [
        'export-query-logs --db-type <DB_TYPE> --host <DB_HOSTNAME> --port <DB_PORT> --user <DB_USERNAME> --password <DB_PASSWORD> --database <DB_NAME> --mode enable',
      ],
      title: 'Enable query logging (Step 1)',
      description: 'Enables query logging. Run this command once to start logging. The script will exit immediately after enabling logging.',
    },
  ],
  disableQueryLogsScript: [
    {
      commands: ['cd export_query_logs'],
      title: 'Move to export query logs directory',
      description: '',
    },
    {
      commands: ['poetry shell'],
      title: 'Activate virtual env',
      description: 'Note: Run "poetry install" only once during initial setup if dependencies are not already installed.',
    },
    {
      commands: [
        'export-query-logs --db-type <DB_TYPE> --host <DB_HOSTNAME> --port <DB_PORT> --user <DB_USERNAME> --password <DB_PASSWORD> --database <DB_NAME> --mode disable --output-dir <OUTPUT_DIRECTORY>',
      ],
      title: 'Disable query logging and export (Step 2)',
      description: 'Disables query logging and exports the captured logs to a CSV file. Run this command after some time to stop logging and retrieve the logs.',
    },
  ],
  queryLogAwsCliInstallScript,
};
